import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { theme } from '../../../core/design-system/theme'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function durationLabel(start, end) {
  if (!start || !end) return ''
  const hours = Math.round((new Date(end) - new Date(start)) / 3600000)
  return `${hours} hour${hours !== 1 ? 's' : ''}`
}

function CheckCircleIcon() {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      role="img"
    >
      <circle cx="32" cy="32" r="32" fill={theme.colors.successBg} />
      <polyline
        points="20 32 28 40 44 24"
        stroke={theme.colors.successText}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function SummaryRow({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '9px 0',
        borderBottom: '1px solid ' + theme.colors.border,
      }}
    >
      <span
        style={{
          fontFamily: theme.fonts.body,
          fontSize: 13,
          color: theme.colors.textSecondary,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: theme.fonts.body,
          fontSize: 13,
          fontWeight: 600,
          color: theme.colors.textPrimary,
          textAlign: 'right',
          maxWidth: '60%',
        }}
      >
        {value}
      </span>
    </div>
  )
}

export default function BookingConfirmedView() {
  const location = useLocation()
  const navigate = useNavigate()
  const booking = location.state?.booking ?? null

  const [primaryHovered, setPrimaryHovered] = useState(false)
  const [secondaryHovered, setSecondaryHovered] = useState(false)

  return (
    <main
      className="page-enter"
      style={{
        background: theme.colors.cream,
        minHeight: '100vh',
        paddingBottom: 90,
        paddingTop: 48,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          padding: '0 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <CheckCircleIcon />

        <h1
          style={{
            fontFamily: theme.fonts.display,
            fontWeight: 600,
            fontSize: 24,
            color: theme.colors.textPrimary,
            margin: '20px 0 8px',
            lineHeight: 1.3,
          }}
        >
          Your Nonna is on her way!
        </h1>
        <p
          style={{
            fontFamily: theme.fonts.body,
            fontSize: 15,
            color: theme.colors.textSecondary,
            margin: '0 0 28px',
          }}
        >
          Booking confirmed. Enjoy your time with the family.
        </p>

        {booking ? (
          <div
            style={{
              width: '100%',
              background: theme.colors.card,
              border: '1px solid ' + theme.colors.border,
              borderRadius: 14,
              padding: 14,
              boxShadow: '0 2px 8px rgba(92,61,46,0.05)',
              marginBottom: 28,
              textAlign: 'left',
            }}
          >
            <SummaryRow label="Booking ID" value={booking.id.slice(0, 8).toUpperCase()} />
            <SummaryRow label="Date & time" value={formatDate(booking.scheduledStart)} />
            <SummaryRow
              label="Duration"
              value={durationLabel(booking.scheduledStart, booking.scheduledEnd)}
            />
            {booking.locationLabel && (
              <SummaryRow label="Location" value={booking.locationLabel} />
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 10,
              }}
            >
              <span
                style={{
                  fontFamily: theme.fonts.body,
                  fontSize: 13,
                  color: theme.colors.textSecondary,
                }}
              >
                Total
              </span>
              <span
                style={{
                  fontFamily: theme.fonts.body,
                  fontSize: 17,
                  fontWeight: 700,
                  color: theme.colors.terracotta,
                }}
              >
                ${Number(booking.totalAmount).toFixed(2)}
              </span>
            </div>
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              background: theme.colors.card,
              border: '1px solid ' + theme.colors.border,
              borderRadius: 14,
              padding: '20px 14px',
              boxShadow: '0 2px 8px rgba(92,61,46,0.05)',
              marginBottom: 28,
            }}
          >
            <p
              style={{
                fontFamily: theme.fonts.body,
                fontSize: 14,
                color: theme.colors.textSecondary,
                margin: 0,
              }}
            >
              Your booking has been confirmed.
            </p>
          </div>
        )}

        <button
          onClick={() => navigate('/')}
          onMouseEnter={() => setPrimaryHovered(true)}
          onMouseLeave={() => setPrimaryHovered(false)}
          style={{
            width: '100%',
            padding: '14px 0',
            borderRadius: 14,
            background: theme.colors.terracotta,
            color: '#FFFFFF',
            border: 'none',
            fontFamily: theme.fonts.body,
            fontWeight: 700,
            fontSize: 15,
            cursor: 'pointer',
            marginBottom: 12,
            opacity: primaryHovered ? 0.88 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          Back to home
        </button>

        <button
          onClick={() => navigate('/bookings')}
          onMouseEnter={() => setSecondaryHovered(true)}
          onMouseLeave={() => setSecondaryHovered(false)}
          style={{
            width: '100%',
            padding: '13px 0',
            borderRadius: 14,
            background: 'transparent',
            color: theme.colors.terracotta,
            border: '1.5px solid ' + theme.colors.terracotta,
            fontFamily: theme.fonts.body,
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
            opacity: secondaryHovered ? 0.75 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          View my bookings
        </button>
      </div>
    </main>
  )
}
